/**
 * The mode registry.
 *
 * One import and one entry per mode, and nothing else. Everything about a mode lives in its own
 * directory under `modes/`, so this stays the list of what exists rather than a second place to
 * describe anything. The menu reads this list, which is why no part of the app ever switches on a
 * mode id.
 */

import { HUB } from '@/modes/hub/hubMode';
import { RAID } from '@/modes/raid/raidMode';
import { SHOOTING_RANGE } from '@/modes/shootingRange/shootingRangeMode';
import { ZONE_LAB } from '@/modes/zoneLab/zoneLabMode';
import type { GameMode } from '@/modes/modeContract';

// The lab is a development tool with a card. The spread folds away in a build, and with it the
// module and every word in it; `tools/bin/bundle-check.sh` fails the build if any survives.
export const MODES: readonly GameMode[] = [
  SHOOTING_RANGE,
  RAID,
  HUB,
  ...(import.meta.env.DEV ? [ZONE_LAB] : []),
];

/**
 * The mode whose scene sits behind the main menu. The first registered mode that offers one.
 *
 * Read off the static flag rather than off a factory, because picking the featured mode is what
 * decides which chunk the menu downloads, and a decision cannot depend on the download it makes.
 */
export function featuredMode(): GameMode | null {
  return MODES.find((mode) => mode.hasBackdrop) ?? MODES[0] ?? null;
}

export function findMode(id: string): GameMode | null {
  return MODES.find((mode) => mode.id === id) ?? null;
}
